import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import { Template } from './hooks/useOrgTemplates';
import useTemplateMutations from './hooks/useTemplateMutations';
import { dialogPaperProps } from './darkModeStyles';

interface TemplatePublishingFlowProps {
  template: Template;
  allTemplates: Template[];
  onRefresh: () => void;
}

/**
 * Find any published template that is in the same version lineage as this one,
 * looking in BOTH directions:
 *
 *  - Ancestors (walk up parentTemplateId): a genuine previous version has a
 *    strictly lower version number.  "Create Draft" always increments, so this
 *    is true for real version history.  "Clone" always resets to v1, so
 *    parent.version is never less than the clone's version — clones excluded.
 *
 *  - Descendants (walk down via children): a genuine newer version has a
 *    strictly higher version number.  Needed for the case where v2 is already
 *    published and the user tries to publish v1 again — v1 has no published
 *    ancestor, but it DOES have a published descendant (v2).
 *
 * System templates are always excluded.
 */
function findPublishedVersionConflict(
  template: Template,
  all: Template[]
): Template | null {
  // Walk UP — find a published ancestor with a lower version
  let currentParentId = template.parentTemplateId;
  while (currentParentId) {
    const parent = all.find((p) => p.id === currentParentId);
    if (!parent) break;
    if (
      !parent.isSystem &&
      parent.status === 'published' &&
      parent.version < template.version
    ) {
      return parent;
    }
    currentParentId = parent.parentTemplateId;
  }

  // Walk DOWN — find a published descendant with a higher version
  const findPublishedDescendant = (t: Template): Template | null => {
    const children = all.filter(
      (c) => c.parentTemplateId === t.id && !c.isSystem && c.version > t.version
    );
    for (const child of children) {
      if (child.status === 'published') return child;
      const deeper = findPublishedDescendant(child);
      if (deeper) return deeper;
    }
    return null;
  };

  return findPublishedDescendant(template);
}

function TemplatePublishingFlow({
  template,
  allTemplates,
  onRefresh,
}: TemplatePublishingFlowProps) {
  const { publishTemplate, unpublishTemplate, createNewVersion } =
    useTemplateMutations();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [versionConflictOpen, setVersionConflictOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [newVersionConfirmOpen, setNewVersionConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const isPublished = template.status === 'published';
  const isUnpublished = !isPublished;

  // Published older version of this same template, if one exists
  const versionConflict = findPublishedVersionConflict(template, allTemplates);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handlePublish = async () => {
    setConfirmOpen(false);
    setVersionConflictOpen(false);
    setPublishing(true);
    try {
      // If another version of this template is published, unpublish it first
      if (versionConflict) {
        await unpublishTemplate(versionConflict.id);
      }
      const result = await publishTemplate(template.id);
      if (result?.success) {
        showSnackbar('Template published successfully', 'success');
        onRefresh();
      } else {
        showSnackbar(result?.error ?? 'Failed to publish template', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to publish template', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setUnpublishConfirmOpen(false);
    setUnpublishing(true);
    try {
      const result = await unpublishTemplate(template.id);
      if (result?.success) {
        showSnackbar('Template unpublished', 'success');
        onRefresh();
      } else {
        showSnackbar(result?.error ?? 'Failed to unpublish template', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to unpublish template', 'error');
    } finally {
      setUnpublishing(false);
    }
  };

  const handleCreateNewVersion = async () => {
    setNewVersionConfirmOpen(false);
    setCreatingVersion(true);
    try {
      const result = await createNewVersion(template.id);
      if (result?.success) {
        showSnackbar('New draft version created', 'success');
        onRefresh();
      } else {
        showSnackbar(result?.error ?? 'Failed to create new version', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to create new version', 'error');
    } finally {
      setCreatingVersion(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {/* Publish button — for unpublished templates */}
      {isUnpublished && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() =>
            versionConflict
              ? setVersionConflictOpen(true)
              : setConfirmOpen(true)
          }
          disabled={publishing}
        >
          {publishing ? 'Publishing…' : 'Publish'}
        </Button>
      )}

      {/* Create Draft button — for published templates */}
      {isPublished && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setNewVersionConfirmOpen(true)}
          disabled={creatingVersion}
        >
          {creatingVersion ? 'Creating…' : 'Create Draft'}
        </Button>
      )}

      {/* Unpublish button — for published templates */}
      {isPublished && (
        <Button
          variant="outlined"
          color="warning"
          size="small"
          onClick={() => setUnpublishConfirmOpen(true)}
          disabled={unpublishing}
        >
          {unpublishing ? 'Unpublishing…' : 'Unpublish'}
        </Button>
      )}

      {/* Auto-save indicator for unpublished templates */}
      {isUnpublished && (
        <Typography
          variant="caption"
          sx={(theme) => ({
            color: theme.custom?.textLight,
            display: 'flex',
            alignItems: 'center',
          })}
        >
          Changes auto-saved
        </Typography>
      )}

      {/* Standard publish confirmation dialog (no version conflict) */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Publish Template</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to publish <strong>v{template.version}</strong> of
            this template.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>This will:</DialogContentText>
          <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2, '& li': { mb: 0.5 } }}>
            <li>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.custom?.textLight })}
              >
                Make v{template.version} an <strong>active template</strong>{' '}
                available for new inspections in your organization
              </Typography>
            </li>
            <li>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.custom?.textLight })}
              >
                Other published templates will <strong>remain published</strong>
              </Typography>
            </li>
            <li>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.custom?.textLight })}
              >
                Existing inspections already in progress will{' '}
                <strong>not</strong> be affected
              </Typography>
            </li>
          </Box>
          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
            Multiple templates can be published simultaneously — inspectors
            choose which template to use when starting an inspection. Only one
            version of each template may be published at a time.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="primary">
            Publish v{template.version}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version conflict dialog — shown when an older version of this template is already published */}
      <Dialog
        open={versionConflictOpen}
        onClose={() => setVersionConflictOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Publish New Version</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to publish <strong>v{template.version}</strong> of
            this template.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Only one version of each template can be published at a time.
            Publishing <strong>v{template.version}</strong> will automatically
            unpublish the currently active{' '}
            <strong>v{versionConflict?.version}</strong>.
          </DialogContentText>
          <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2, '& li': { mb: 0.5 } }}>
            <li>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.custom?.textLight })}
              >
                <strong>v{versionConflict?.version}</strong> will be unpublished
                automatically
              </Typography>
            </li>
            <li>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.custom?.textLight })}
              >
                <strong>v{template.version}</strong> will become the active
                version for new inspections
              </Typography>
            </li>
            <li>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.custom?.textLight })}
              >
                Existing inspections already in progress will{' '}
                <strong>not</strong> be affected
              </Typography>
            </li>
          </Box>
          <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
            While multiple templates can be published simultaneously, only one
            version of each template may be active at a time.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionConflictOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="primary">
            Publish v{template.version}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unpublish confirmation dialog */}
      <Dialog
        open={unpublishConfirmOpen}
        onClose={() => setUnpublishConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Unpublish Template</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to unpublish{' '}
            <strong>v{template.version}</strong>?
          </DialogContentText>
          <DialogContentText>
            This template will no longer be used for new inspections. You can
            republish it at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpublishConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleUnpublish} variant="contained" color="warning">
            Unpublish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create new draft version confirmation dialog */}
      <Dialog
        open={newVersionConfirmOpen}
        onClose={() => setNewVersionConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle>Create Draft Version</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will create a new draft copy of{' '}
            <strong>v{template.version}</strong> that you can edit without
            affecting the currently published template.
          </DialogContentText>
          <DialogContentText>
            The published version will remain active until you publish the new
            draft.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVersionConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewVersion}
            variant="contained"
            color="primary"
          >
            Create Draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TemplatePublishingFlow;
